import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronLeft, Info } from "lucide-react";

const TERMS_URL = "https://a.pglm.org.uk/Middlesex_Connect_Terms_and_Conditions.html";

export default function RegisterPage() {
  const nav = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [email, setEmail] = useState("");
  const [glRef, setGlRef] = useState("");
  const [password, setPassword] = useState("");
  const [tcAccepted, setTcAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!firstName.trim() || !familyName.trim() || !email.trim() || !glRef.trim() || password.length < 6) {
      return setError("Please fill all fields (password ≥ 6 chars)");
    }
    if (!tcAccepted) return setError("Please accept the Terms & Conditions to continue.");
    setLoading(true);
    try {
      const r = await apiFetch("/auth/register", {
        method: "POST",
        body: JSON.stringify({
          first_name: firstName.trim(),
          family_name: familyName.trim(),
          gl_ref: glRef.trim(),
          email: email.trim().toLowerCase(),
          password,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Registration failed");
      const devOtp = data.dev_otp ? `&dev_otp=${data.dev_otp}` : "";
      nav(`/verify-otp?email=${encodeURIComponent(data.email)}&purpose=${data.purpose}${devOtp}`, { replace: true });
    } catch (err) {
      setError(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-start justify-center px-4 py-10">
      <div className="max-w-md w-full">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-primary mb-4" data-testid="register-back">
          <ChevronLeft size={16} /> Back
        </Link>
        <Card>
          <CardContent className="p-6 space-y-4">
            <h1 className="text-2xl font-semibold">Create your account</h1>
            <p className="text-sm text-muted-foreground">
              For the fastest activation, please use your official name, Provincial email
              and Grand Lodge reference. Tap <strong>Info</strong> below for a quick guide.
            </p>
            <button
              type="button"
              onClick={() => setInfoOpen((v) => !v)}
              className="w-full flex items-center gap-3 border border-primary/40 rounded-md bg-primary/5 text-primary px-3 py-2 text-sm font-medium"
              data-testid="register-info-button"
            >
              <Info size={18} /> Info — how to activate your account
            </button>
            {infoOpen && (
              <div className="rounded-md border border-border bg-muted/40 p-3 text-sm space-y-2">
                <p>The quickest way to activate is by entering your Grand Lodge Reference Number (GL Ref).</p>
                <p>Please also use your official first name, surname and the email address you normally use with the Province (Portal / Hermes / Adelphi).</p>
                <p>If details match our records, activation is automatic after email verification. Otherwise, an admin reviews it.</p>
              </div>
            )}
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>First name</Label>
                  <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="register-first-name" placeholder="John" />
                </div>
                <div className="space-y-1">
                  <Label>Family name</Label>
                  <Input value={familyName} onChange={(e) => setFamilyName(e.target.value)} data-testid="register-family-name" placeholder="Smith" />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Grand Lodge reference (GL Ref)</Label>
                <Input value={glRef} onChange={(e) => setGlRef(e.target.value.toUpperCase())} data-testid="register-gl-ref" placeholder="e.g. A123456" />
              </div>
              <div className="space-y-1">
                <Label>Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" placeholder="you@example.com" />
              </div>
              <div className="space-y-1">
                <Label>Password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" placeholder="At least 6 characters" />
              </div>
              {error && <div className="text-sm text-destructive" data-testid="register-error">{error}</div>}
              <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={tcAccepted} onCheckedChange={(v) => setTcAccepted(!!v)} data-testid="register-tc-checkbox" />
                <span>
                  I agree to the{" "}
                  <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="text-primary underline" data-testid="register-tc-view">
                    Terms &amp; Conditions
                  </a>.
                </span>
              </label>
              <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full">
                {loading ? "Creating…" : "Create account"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
